const React = require('react');

const { useEffect, useState } = React;
const T = require('prop-types');
const { useWatch } = require('react-hook-form');

const { useForm } = require('hooks/useForm');

const { default: Classes } = require('./styles.scss');

const LoadingScreen = require('components/LoadingScreen');
const BottomButtonContainer = require('components/BottomButtonContainer');
const SurveyForm = require('../SurveyForm');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: ArrowBack } = require('@mui/icons-material/ArrowBack');
const { default: Tooltip } = require('@mui/material/Tooltip');
const Moment = require('moment/moment');

const PreventNavigationDialog = require('components/PreventNavigationDialog');
const IsEqual = require('lodash/isEqual');
const { usePrevious } = require('../../../../../hooks/usePrevious');
const { analyticsTemplates } = require('utils/analytics');
const SurveyEditPageFormConfig = require('./formConfig');

const SurveyEditPage = (props) => {

    // eslint-disable-next-line no-empty-pattern
    const {
        goBack,
        onSubmit,
        onClickResetSearchFilters,
        activeSearchFilter,
        interestsActiveFilter_survey,
        badges,
        departments,
        yearsHiredList,
        officesList,
        isCompany,
        isCommunity,
        isSchoolOnline,
        schoolRoles,
        schoolRoleGroups,
        searchResults,
        survey,
        history,
        majors
    } = props;

    const [hasSubmitted, setHasSubmitted] = useState(false);

    const onSubmitForm = (data) => {

        setHasSubmitted(true);
        analyticsTemplates.buttons('edit survey', `surveys: edit survey request surveyId:${data.id}`);
        onSubmit(SurveyEditPageFormConfig.buildSubmitPayload(data));
    };

    const {
        methods,
        renderForm
    } = useForm(SurveyEditPageFormConfig, {
        onSubmit: onSubmitForm
    });

    const {
        control,
        handleSubmit,
        formState: {
            errors,
            isDirty,
            isValid,
            isSubmitting,
            isSubmitSuccessful
        },
        setValue,
        reset
    } = methods;

    const watchStartDate = useWatch({ control, name: 'startDate' });
    const watchEndDate = useWatch({ control, name: 'endDate' });
    const manuallyStopVal = useWatch({ control, name: 'manuallyStop' });
    const questions = useWatch({ control, name: 'questions' }) ?? [];

    const hasRequiredDataForSubmit = questions.length > 0
        && questions.every(({ answers = [], answerType, text }) => {

            let hasMinAnswers = false;

            switch (answerType) {
                case 'freeInput':
                    hasMinAnswers = true;
                    break;

                case 'select':
                    hasMinAnswers = answers.length > 0;
                    break;

                case 'checkbox':
                    hasMinAnswers = answers.length > 1;
                    break;
            }

            return Boolean(text) &&
                hasMinAnswers &&
                answers.every((a) => Boolean(a?.text));
        });

    const prevSearchResult = usePrevious(searchResults);
    const prevSurveyResult = usePrevious(survey);

    useEffect(() => {

        if (Moment(watchStartDate).isAfter(Moment(watchEndDate)) && isDirty && !manuallyStopVal) {
            setValue('endDate', Moment(watchStartDate).add(1, 'days').format('YYYY-MM-DD'), {
                shouldDirty: true,
                shouldValidate: true
            });
        }
    }, [isDirty, manuallyStopVal, setValue, watchEndDate, watchStartDate]);

    useEffect(() => {

        if (survey){

            if (!IsEqual(survey,prevSurveyResult) && !isDirty) {
                const surveyWithFormattedDates = {
                    ...survey,
                    startDate: survey.startDate ? String(survey.startDate).split('T')[0] : null,
                    endDate: survey.endDate ? String(survey.endDate).split('T')[0] : null
                };
                reset(surveyWithFormattedDates);
            }
        }
    }, [isDirty, prevSurveyResult, reset, survey]);

    useEffect(() => {

        if (searchResults !== null  && !IsEqual(searchResults,prevSearchResult)) {
            setValue('selectedUsers', searchResults, {
                shouldDirty: true,
                shouldValidate: true
            });
        }
    }, [prevSearchResult, searchResults, setValue]);

    return (
        <BottomButtonContainer
            btnLabel='Save'
            formId={SurveyEditPageFormConfig.formId}
            disabled={!hasRequiredDataForSubmit || isSubmitting || isSubmitSuccessful}
        >
            <PreventNavigationDialog
                saveFunction={handleSubmit(onSubmitForm)}
                alertMsg={'You have made changes.\n Do you want to save or discard them?'}
                when={isDirty && !hasSubmitted}
                navigate={(path, state) => history.push(path, state)}
                shouldBlockNavigation={() => {

                    if ((isDirty || !isValid) && !hasSubmitted) {
                        return true;
                    }

                    return false;
                }}
                pageHasErrors={!isValid}
            />
            <div className={Classes.wrapper}>
                <div className={Classes.innerWrapper}>

                    <div className={Classes.topRow}>
                        <Tooltip title={'Go Back'} arrow={true} placement={'top'}>
                            <IconButton onClick={goBack} aria-label='previous screen' size='large'><ArrowBack /></IconButton>
                        </Tooltip>

                        <h2>Edit Survey</h2>
                    </div>
                    {survey && survey.questions && survey.questions.length ? renderForm(
                        <SurveyForm
                            control={control}
                            setValue={setValue}
                            reset={reset}
                            isEdit={true}
                            errors={errors}
                            onClickResetSearchFilters={onClickResetSearchFilters}
                            activeSearchFilter={activeSearchFilter}
                            interestsActiveFilter_survey={interestsActiveFilter_survey}
                            badges={badges}
                            searchResults={searchResults}
                            departments={departments}
                            officesList={officesList}
                            yearsHiredList={yearsHiredList}
                            isCompany={isCompany}
                            isCommunity={isCommunity}
                            isSchoolOnline={isSchoolOnline}
                            schoolRoles={schoolRoles}
                            schoolRoleGroups={schoolRoleGroups}
                            majors={majors}
                        />
                    ) : <LoadingScreen variant='content' />}
                </div>
            </div>
        </BottomButtonContainer>
    );
};

SurveyEditPage.propTypes = {
    goBack: T.func,
    onSubmit: T.func,
    onClickResetSearchFilters: T.func,
    activeSearchFilter: T.object,
    interestsActiveFilter_survey: T.array,
    badges: T.array,
    searchResults: T.array,
    departments: T.array,
    officesList: T.arrayOf(
        T.shape({
            id: T.number,
            name: T.string
        })
    ),
    yearsHiredList: T.arrayOf(
        T.shape({
            id: T.number,
            year: T.number
        })
    ),
    isCompany: T.bool,
    isCommunity: T.bool,
    isSchoolOnline: T.bool,
    schoolRoles: T.array,
    schoolRoleGroups: T.array,
    history: T.object,
    survey: T.object,
    majors: T.arrayOf(T.shape({
        id: T.number,
        name: T.string
    }))
};

module.exports = SurveyEditPage;
